import { PERMISSIONS, ROLE_PERMISSIONS } from '../constants/permissions';
import { MANAGER_ACCESS_LEVELS, ROLES, normalizeManagerAccessLevel, normalizeRole } from '../constants/roles';

const LEGACY_PERMISSION_CAPABILITY_MAP = {
  [PERMISSIONS.DASHBOARD_VIEW]: ['account.profile.read'],
  [PERMISSIONS.ONBOARDING_VIEW]: ['platform.societies.read'],
  [PERMISSIONS.ONBOARDING_MANAGE]: ['platform.societies.read'],
  [PERMISSIONS.SUPPORT_VIEW]: ['support.ticket.read'],
  [PERMISSIONS.SUPPORT_MANAGE]: ['support.ticket.status.update'],
  [PERMISSIONS.CONTACT_INQUIRIES_VIEW]: ['platform.societies.read'],
  [PERMISSIONS.CONTACT_INQUIRIES_MANAGE]: ['platform.societies.update'],
  [PERMISSIONS.SETTINGS_VIEW]: ['account.profile.read'],
  [PERMISSIONS.SETTINGS_PROFILE_EDIT]: ['account.profile.update'],
  [PERMISSIONS.SETTINGS_SECURITY_EDIT]: ['account.security.password.update'],
  [PERMISSIONS.SETTINGS_PREFERENCES_EDIT]: ['account.preferences.update'],
  [PERMISSIONS.SETTINGS_NOTIFICATIONS_EDIT]: ['account.notifications.update'],
  [PERMISSIONS.SETTINGS_SOCIETY_VIEW]: ['platform.societies.read'],
  [PERMISSIONS.SETTINGS_SOCIETY_EDIT]: ['platform.societies.update'],
  [PERMISSIONS.SETTINGS_TEAM_VIEW]: ['platform.iam.admin.read', 'society.team.read'],
  [PERMISSIONS.SETTINGS_TEAM_MANAGE]: ['platform.iam.admin.invite', 'society.team.invite'],
  [PERMISSIONS.SETTINGS_OPERATIONS_VIEW]: ['society.settings.operations.read'],
  [PERMISSIONS.SETTINGS_OPERATIONS_EDIT]: ['society.settings.operations.update'],
  [PERMISSIONS.SETTINGS_SOCIETY_OPERATIONS_VIEW]: ['society.settings.operations.read'],
  [PERMISSIONS.SETTINGS_SOCIETY_OPERATIONS_EDIT]: ['society.settings.operations.update'],
  [PERMISSIONS.SETTINGS_GUARD_POLICY_VIEW]: ['society.settings.guardPolicy.read'],
  [PERMISSIONS.SETTINGS_GUARD_POLICY_EDIT]: ['society.settings.guardPolicy.update'],
  [PERMISSIONS.OPERATION_BLOCKS_VIEW]: ['platform.operationBlocks.read'],
  [PERMISSIONS.OPERATION_BLOCKS_MANAGE]: ['platform.operationBlocks.manage'],
  [PERMISSIONS.APP_VERSION_POLICIES_VIEW]: ['platform.appVersions.read'],
  [PERMISSIONS.APP_VERSION_POLICIES_MANAGE]: ['platform.appVersions.manage'],
  [PERMISSIONS.SETTINGS_PLATFORM_VIEW]: ['platform.settings.policy.read'],
  [PERMISSIONS.SETTINGS_PLATFORM_EDIT]: ['platform.settings.policy.update'],
  [PERMISSIONS.MANAGERS_VIEW]: ['society.team.read'],
  [PERMISSIONS.MANAGERS_MANAGE]: ['society.team.invite'],
  [PERMISSIONS.LOGS_VIEW]: ['account.profile.read'],
  [PERMISSIONS.LOGS_EXPORT]: ['audit.export'],
  [PERMISSIONS.AUDIT_LOGS_VIEW]: ['audit.read'],
  [PERMISSIONS.USERS_VIEW]: ['society.settings.operations.read', 'platform.societies.read'],
  [PERMISSIONS.USERS_EXPORT]: ['society.settings.operations.read', 'platform.societies.read'],
  [PERMISSIONS.USERS_MANAGE]: ['society.settings.operations.update', 'platform.societies.update'],
  [PERMISSIONS.VEHICLES_EXPORT]: ['society.settings.operations.read', 'platform.societies.read'],
  [PERMISSIONS.INVENTORY_SOCIETY_VIEW]: ['society.settings.operations.read', 'platform.societies.read'],
  [PERMISSIONS.INVENTORY_SOCIETY_MANAGE]: ['society.settings.operations.update', 'platform.societies.update'],
  [PERMISSIONS.INVENTORY_GLOBAL_VIEW]: ['platform.societies.read'],
  [PERMISSIONS.INVENTORY_GLOBAL_MANAGE]: ['platform.societies.update'],
  [PERMISSIONS.CONTROLLERS_VIEW]: ['controller.diagnostics.read'],
  [PERMISSIONS.CONTROLLERS_MANAGE]: ['controller.config.update'],
  [PERMISSIONS.SOCIETIES_VIEW]: ['platform.societies.read'],
  [PERMISSIONS.SOCIETIES_MANAGE]: ['platform.societies.create', 'platform.societies.update'],
  [PERMISSIONS.SOCIETY_ADMINS_MANAGE]: ['platform.iam.admin.invite', 'society.team.invite'],
};

const READ_ONLY_MANAGER_BLOCKED_PERMISSIONS = new Set([
  PERMISSIONS.USERS_MANAGE,
  PERMISSIONS.INVENTORY_SOCIETY_MANAGE,
  PERMISSIONS.SETTINGS_SOCIETY_OPERATIONS_EDIT,
]);

const fallbackAllowed = () => process.env.NODE_ENV !== 'production';

export const resolveCapabilityKeys = (permission) => {
  if (!permission) return [];
  return LEGACY_PERMISSION_CAPABILITY_MAP[permission] || [permission];
};

export const hasCapability = (capabilities, permission) => {
  if (!permission || capabilities?.status !== 'loaded') return false;

  const granted = new Set(capabilities.permissions || []);
  return resolveCapabilityKeys(permission).some((permissionKey) => granted.has(permissionKey));
};

export const hasAnyCapability = (capabilities, permissions = []) => {
  return permissions.some((permission) => hasCapability(capabilities, permission));
};

export const getPermissionsForUser = (role, user) => {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return [];

  // Legacy fallback only. Backend `/auth/capabilities` is production authorization source.
  const allowedPermissions = ROLE_PERMISSIONS[normalizedRole] || [];

  if (
    normalizedRole === ROLES.SOCIETY_MANAGER &&
    normalizeManagerAccessLevel(user?.managerAccessLevel) === MANAGER_ACCESS_LEVELS.READ_ONLY
  ) {
    return allowedPermissions.filter((permission) => !READ_ONLY_MANAGER_BLOCKED_PERMISSIONS.has(permission));
  }

  return allowedPermissions;
};

export const can = (role, permission, user, capabilities) => {
  if (!permission) return false;
  if (capabilities?.status === 'loaded') {
    return hasCapability(capabilities, permission);
  }

  if (capabilities && !fallbackAllowed()) {
    return false;
  }

  return getPermissionsForUser(role, user).includes(permission);
};

export const canAny = (role, permissions = [], user, capabilities) => {
  if (capabilities?.status === 'loaded') {
    return hasAnyCapability(capabilities, permissions);
  }

  if (capabilities && !fallbackAllowed()) {
    return false;
  }

  return permissions.some((permission) => can(role, permission, user));
};

export const getDefaultRouteForRole = (role) => {
  const normalizedRole = normalizeRole(role);

  if (normalizedRole === ROLES.SUPER_ADMIN) return '/societies';
  if (normalizedRole === ROLES.ACCOUNT_MANAGER) return '/onboarding';
  if (normalizedRole === ROLES.SOCIETY_ADMIN || normalizedRole === ROLES.SOCIETY_MANAGER) {
    return '/logs';
  }
  if (normalizedRole === ROLES.SOCIETY_GUARD) return '/logs';
  if (normalizedRole === ROLES.DEVELOPER) return '/controllers';
  if (normalizedRole === ROLES.TECHNICIAN || normalizedRole === ROLES.INSTALLATION_MANAGER) {
    return '/controllers';
  }

  return '/support';
};
